/**
 * user-profile-api.ts
 *
 * Handles:
 * - Login initiation (PKCE + redirect to B2C)
 * - Profile fetch
 * - Logout URL generation
 *
 * Token exchange and refresh are handled server-side via Next.js API routes
 * so the client_secret and refresh_token never touch the browser.
 */

import { generateCodeVerifier, generateCodeChallenge } from '../auth/pkce';
import { SIGNUP_OR_SIGNIN_POLICY } from '@/components/constants/user-auth';
import { getLocale } from '../utils';
import { unlinkWebPushContact } from '@/lib/emarsys/web-push';

// ---------------------------------------------------------------------------
// Login — initiates PKCE redirect to B2C
// ---------------------------------------------------------------------------

/**
 * Builds the B2C /authorize URL with PKCE and redirects the browser.
 *
 * Flow:
 * 1. Generate code_verifier + code_challenge
 * 2. Store verifier in sessionStorage (short-lived, same-origin only)
 * 3. Redirect to B2C /authorize with code_challenge
 * 4. B2C redirects back to redirect_uri with ?code=xxx
 * 5. Browser sends code + verifier to /api/auth/callback (Next.js API route)
 * 6. Next.js API route forwards to Azure Function (which has client_secret)
 * 7. Azure Function returns tokens → Next.js stores refresh_token in httpOnly cookie
 */
export const initiateLogin = async (
  node_locale: string,
  brandId: string,
  config: any,
  currentPath: string,
): Promise<void> => {
  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);

  // Store verifier in sessionStorage — only readable by same origin
  // Cleared immediately after code exchange in the callback handler
  sessionStorage.setItem('pkce_verifier', verifier);
  // Store current path for post-login redirect restore
  // localStorage.setItem('current_path', currentPath);

  const redirectURL = config?.NEXT_PUBLIC_ICIM_REGISTERED_REDIRECT_URL || '';
  const CLIENT_ID = process.env.NEXT_PUBLIC_ICIM_CLIENT_ID || '';
  const TENANT_ID = process.env.NEXT_PUBLIC_ICIM_TENANT_ID || '';
  const BASE_URL = process.env.NEXT_PUBLIC_ICIM_BASE_URL || '';
  const POLICY = SIGNUP_OR_SIGNIN_POLICY;
  const locale = getLocale(node_locale);

  const authUrl =
    `${BASE_URL}/${TENANT_ID}/oauth2/v2.0/authorize` +
    `?p=${POLICY}` +
    `&client_id=${CLIENT_ID}` +
    `&nonce=${crypto.randomUUID()}` +
    `&redirect_uri=${encodeURIComponent(redirectURL)}` +
    `&scope=openid%20offline_access` +
    `&response_type=code` +
    `&code_challenge=${challenge}` +
    `&code_challenge_method=S256` +
    `&brand_id=${brandId}` +
    `&ui_locales=${locale}` +
    `&state=${encodeURIComponent(currentPath)}`;

  window.location.href = authUrl;
};

// ---------------------------------------------------------------------------
// Profile fetch
// ---------------------------------------------------------------------------

/**
 * Fetches the user profile from the app-wrapper API using the id_token.
 * Called after successful token exchange and on session restore.
 */
export const getProfileDetails = async (brandId: string): Promise<any> => {
  const queryParams = new URLSearchParams({
    action: 'profile',
    mpId: brandId,
  }).toString();

  const response = await fetch(`/api/app-wrapper?${queryParams}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
      // 'X-ICIM-JWT': authToken,
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(
      `Get User Profile API Error ${response.status}: ${errorBody?.errorMessage}`,
    );
  }

  return response.json();
};

// ---------------------------------------------------------------------------
// Logout URL
// ---------------------------------------------------------------------------

/**
 * Builds the B2C logout URL.
 * Used to redirect the user to B2C to clear their B2C session.
 */
export const createIcimLogoutURL = (config: any): string => {
  const redirectURL = config?.NEXT_PUBLIC_ICIM_REGISTERED_REDIRECT_URL || '';
  const encodedRedirectUri = encodeURIComponent(redirectURL);
  const TENANT_ID = process.env.NEXT_PUBLIC_ICIM_TENANT_ID || '';
  const BASE_URL = process.env.NEXT_PUBLIC_ICIM_BASE_URL || '';
  const POLICY = SIGNUP_OR_SIGNIN_POLICY;

  // id_token_hint is no longer needed — Next.js logout route clears the cookie
  return (
    `${BASE_URL}/${TENANT_ID}/${POLICY}/oauth2/v2.0/logout` +
    `?post_logout_redirect_uri=${encodedRedirectUri}`
  );
};

// Bounded so a slow Emarsys call never blocks the logout redirect.
const waitForPushUnlink = (unlink: Promise<void>) =>
  Promise.race([
    unlink,
    new Promise<void>((resolve) => setTimeout(resolve, 2000)),
  ]);

export const logOutUser = async (): Promise<void> => {
  try {
    // Server reads id_token from httpOnly cookie and builds the logout URL
    const res = await fetch('/api/auth/logout', {
      keepalive: true, // ← prevents Safari from cancelling during navigation
    });
    if (res.ok) {
      const unlink = unlinkWebPushContact().catch(() => undefined);
      const { logoutUrl } = await res.json();
      // clear BGE auth
      localStorage.removeItem('gbe_authentication');
      localStorage.removeItem('gbe_userCode');
      await waitForPushUnlink(unlink);
      // Redirect to B2C logout with id_token_hint
      window.location.href = logoutUrl;
    }
  } catch (error) {
    console.error('[Auth] Logout error:', error);
    // Fallback — redirect home even if logout URL fetch fails
    window.location.href = '/';
  }
};

// ---------------------------------------------------------------------------
// Redirect URL (kept for backwards compatibility if needed elsewhere)
// ---------------------------------------------------------------------------

/**
 * @deprecated Use initiateLogin() instead — this does not include PKCE.
 */
export const createIcimRedirectURL = (
  node_locale: string,
  brandId: string,
  config: any,
  currentPath: string,
): string => {
  const redirectURL = config?.NEXT_PUBLIC_ICIM_REGISTERED_REDIRECT_URL || '';
  const encodedRedirectUri = encodeURIComponent(redirectURL);
  const CLIENT_ID = process.env.NEXT_PUBLIC_ICIM_CLIENT_ID || '';
  const TENANT_ID = process.env.NEXT_PUBLIC_ICIM_TENANT_ID || '';
  const BASE_URL = process.env.NEXT_PUBLIC_ICIM_BASE_URL || '';
  const POLICY = SIGNUP_OR_SIGNIN_POLICY;
  const locale = getLocale(node_locale);

  return (
    `${BASE_URL}/${TENANT_ID}/oauth2/v2.0/authorize` +
    `?p=${POLICY}` +
    `&client_id=${CLIENT_ID}` +
    `&nonce=defaultNonce` +
    `&redirect_uri=${encodedRedirectUri}` +
    `&scope=openid` +
    `&response_type=code` +
    `&brand_id=${brandId}` +
    `&ui_locales=${locale}` +
    `&state=${currentPath}`
  );
};

